import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { ExternalDataService, WhatsAppCampaignSendLogPaginatedResponse } from '../../../services/externalDataService.service';
import { CampaignOutreachSummaryDto, MarketingAttributionService } from '../../../services/marketing-attribution.service';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { DateInputComponent } from '../../shared/date-input/date-input.component';

type SendLogTab = 'log' | 'conversions';

const OUTREACH_LABELS: Record<string, string> = {
  NoSignupWinBack: 'No-Signup Win-Back',
  NoBookingWinBack: 'No-Booking Win-Back',
  GeoZoneLaunch: 'Geo-Zone Launch (all zones)',
  Total: 'All campaigns (each person once)'
};

// A campaign filter is one of: everything, a fixed campaign id, or an id prefix (geo-zone launch
// campaigns are one per zone — "GeoZoneLaunch:{zoneId}" — so they can only be matched by prefix).
interface CampaignOption {
  key: string;
  label: string;
  campaignId?: string;
  campaignIdPrefix?: string;
}

const GEO_ZONE_LAUNCH_PREFIX = 'GeoZoneLaunch:';

@Component({
  selector: 'app-whatsapp-send-log',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, DateInputComponent],
  templateUrl: './whatsapp-send-log.component.html',
  styleUrl: './whatsapp-send-log.component.css'
})
export class WhatsAppSendLogComponent implements OnInit, OnDestroy {
  campaignOptions: CampaignOption[] = [
    { key: 'all', label: 'All campaigns' },
    { key: 'NoSignupWinBack', label: 'No-Signup Win-Back', campaignId: 'NoSignupWinBack' },
    { key: 'NoBookingWinBack', label: 'No-Booking Win-Back', campaignId: 'NoBookingWinBack' },
    { key: 'geoZoneLaunch', label: 'Geo-Zone Launch (all zones)', campaignIdPrefix: GEO_ZONE_LAUNCH_PREFIX }
  ];

  selectedCampaignKey = this.campaignOptions[0].key;
  statusFilter = '';

  response: WhatsAppCampaignSendLogPaginatedResponse | null = null;
  loading = false;
  errorMessage = '';

  currentPage = 1;
  pageSize = 20;

  // "Conversions" tab: how many people we messaged signed up / booked afterwards. The date range
  // filters by when the message was SENT, not when the signup/booking happened.
  activeTab: SendLogTab = 'log';
  outreach: CampaignOutreachSummaryDto | null = null;
  outreachLoading = false;
  outreachError = '';
  outreachFromDate = '';
  outreachToDate = '';

  private destroy$ = new Subject<void>();
  private loadSubscription$ = new Subject<void>();

  constructor(
    private externalDataService: ExternalDataService,
    private marketingAttributionService: MarketingAttributionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadSendLog();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadSubscription$.next();
    this.loadSubscription$.complete();
  }

  loadSendLog(): void {
    this.loading = true;
    this.errorMessage = '';

    const pageIndex = this.currentPage - 1;

    this.loadSubscription$.next();
    this.loadSubscription$.complete();
    this.loadSubscription$ = new Subject<void>();

    const selected = this.campaignOptions.find(o => o.key === this.selectedCampaignKey) ?? this.campaignOptions[0];
    const status = this.statusFilter || undefined;

    const request$ = selected.campaignId
      ? this.externalDataService.getWhatsAppCampaignSendLog(selected.campaignId, status, pageIndex, this.pageSize)
      : this.externalDataService.getAllWhatsAppCampaignSendLog(selected.campaignIdPrefix, status, pageIndex, this.pageSize);

    request$
      .pipe(takeUntil(this.loadSubscription$), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.response = response;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading WhatsApp campaign send log:', error);
          this.loading = false;
          this.response = null;
          this.errorMessage = 'Failed to load send log. Please try again.';
        }
      });
  }

  setTab(tab: SendLogTab): void {
    this.activeTab = tab;
    // Lazy: only hit the conversions endpoint the first time that tab is opened.
    if (tab === 'conversions' && !this.outreach && !this.outreachLoading) {
      this.resetOutreachDates();
      this.loadOutreach();
    }
  }

  loadOutreach(): void {
    this.outreachLoading = true;
    this.outreachError = '';

    const from = this.outreachFromDate ? new Date(this.outreachFromDate + 'T00:00:00').toISOString() : undefined;
    const to = this.outreachToDate ? new Date(this.outreachToDate + 'T23:59:59').toISOString() : undefined;

    this.marketingAttributionService.getCampaignOutreachResults(from, to)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary) => {
          this.outreach = summary;
          this.outreachLoading = false;
        },
        error: (error) => {
          console.error('Error loading campaign outreach results:', error);
          this.outreachLoading = false;
          this.outreach = null;
          this.outreachError = 'Failed to load conversions. Please try again.';
        }
      });
  }

  applyOutreachFilters(): void {
    this.loadOutreach();
  }

  clearOutreachFilters(): void {
    this.resetOutreachDates();
    this.loadOutreach();
  }

  outreachLabel(campaign: string): string {
    return OUTREACH_LABELS[campaign] ?? campaign;
  }

  private resetOutreachDates(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    this.outreachToDate = today.toISOString().substring(0, 10);
    this.outreachFromDate = thirtyDaysAgo.toISOString().substring(0, 10);
  }

  onCampaignChange(): void {
    this.currentPage = 1;
    this.loadSendLog();
  }

  onStatusFilterChange(): void {
    this.currentPage = 1;
    this.loadSendLog();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadSendLog();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadSendLog();
  }

  // Friendly name for a row's campaign; geo-zone launch ids embed a zone id, so they collapse to one label.
  campaignLabel(campaignId: string): string {
    if (campaignId.startsWith(GEO_ZONE_LAUNCH_PREFIX)) {
      return 'Geo-Zone Launch';
    }
    return this.campaignOptions.find(o => o.campaignId === campaignId)?.label ?? campaignId;
  }

  get totalPages(): number {
    return Math.ceil((this.response?.totalCount || 0) / this.pageSize) || 1;
  }
}
